import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, Briefcase, FileText, MapPin, IndianRupee, Users,
  Calendar, ExternalLink, Mail, Phone, Building2, Search,
} from "lucide-react";

const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];
const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const empty = {
  id: "", institute_id: "", title: "", description: "", location: "",
  employment_type: "Full-time", qualification: "", salary_range: "",
  openings: 1, deadline: "", status: "Open",
};

function fmt(s) { if (!s) return ""; try { return new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } }

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [appsFor, setAppsFor] = useState(null);
  const [apps, setApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [instituteFilter, setInstituteFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [j, t] = await Promise.all([
        api.get("/admin/jobs"),
        api.get("/public/trust/jobs").catch(() => ({ data: { institutes: [] } })),
      ]);
      setJobs(j.data);
      setInstitutes(t.data.institutes || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openAdd = () => { setForm({ ...empty, institute_id: institutes[0]?.id || "" }); setOpen(true); };
  const openEdit = (j) => {
    setForm({
      id: j.id, institute_id: j.institute_id, title: j.title || "", description: j.description || "",
      location: j.location || "", employment_type: j.employment_type || "Full-time",
      qualification: j.qualification || "", salary_range: j.salary_range || "",
      openings: j.openings || 1, deadline: (j.deadline || "").slice(0, 10),
      status: j.status || "Open",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.institute_id) return toast.error("Please choose the institute");
    if (!form.title.trim()) return toast.error("Title is required");
    const payload = {
      institute_id: form.institute_id,
      title: form.title.trim(),
      description: (form.description || "").trim(),
      location: (form.location || "").trim(),
      employment_type: EMPLOYMENT_TYPES.includes(form.employment_type) ? form.employment_type : "Full-time",
      qualification: (form.qualification || "").trim(),
      salary_range: (form.salary_range || "").trim(),
      openings: Math.max(1, Number(form.openings) || 1),
      deadline: (form.deadline || "").slice(0, 10),
      status: form.status === "Closed" ? "Closed" : "Open",
    };
    setSaving(true);
    try {
      if (form.id) { await api.put(`/admin/jobs/${form.id}`, payload); toast.success("Job updated"); }
      else { await api.post("/admin/jobs", payload); toast.success("Job posted"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try { await api.delete(`/admin/jobs/${confirmDelete.id}`); toast.success("Job removed"); setConfirmDelete(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed"); }
  };

  const viewApps = async (j) => {
    setAppsFor(j); setAppsLoading(true);
    try { const { data } = await api.get(`/admin/job-applications`, { params: { job_id: j.id } }); setApps(data || []); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load applications"); }
    finally { setAppsLoading(false); }
  };

  const filtered = useMemo(() => {
    let list = jobs;
    if (instituteFilter) list = list.filter((j) => j.institute_id === instituteFilter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((j) =>
      (j.title || "").toLowerCase().includes(q) ||
      (j.location || "").toLowerCase().includes(q) ||
      (j.institute_name || "").toLowerCase().includes(q) ||
      (j.description || "").toLowerCase().includes(q)
    );
    return list;
  }, [jobs, instituteFilter, search]);

  return (
    <div className="space-y-6" data-testid="admin-jobs">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">Trust CMS</div>
          <h1 className="font-serif text-2xl text-[#1c1a17]">Jobs (Trust-wide)</h1>
          <div className="text-sm text-[#6b655b] mt-0.5">Post openings on behalf of any institute. Also visible on gsmandal.org/careers.</div>
        </div>
        <button type="button" onClick={openAdd} data-testid="adm-jobs-add-btn" className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 text-sm font-semibold rounded-sm">
          <Plus size={16} /> Post New Job
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white rounded-sm border border-[#e5e0d8] px-3 py-1.5 flex-1 min-w-[220px]">
          <Search size={14} className="text-[#6b655b]" />
          <input data-testid="adm-jobs-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, location, institute…" className="flex-1 text-sm bg-transparent focus:outline-none" />
        </div>
        <select data-testid="adm-jobs-filter-institute" value={instituteFilter} onChange={(e) => setInstituteFilter(e.target.value)} className="text-sm bg-white border border-[#e5e0d8] rounded-sm px-3 py-2 min-w-[220px]">
          <option value="">All Institutes ({jobs.length})</option>
          {institutes.map((i) => <option key={i.id} value={i.id}>{i.name} ({jobs.filter((j) => j.institute_id === i.id).length})</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-[#6b655b]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-sm border border-[#e5e0d8] p-10 text-center">
          <Briefcase size={36} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
          <div className="mt-3 text-sm text-[#6b655b]">No jobs yet. Click <b>Post New Job</b> to add one.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((j) => (
            <div key={j.id} data-testid={`adm-job-row-${j.id}`} className="bg-white border border-[#e5e0d8] rounded-sm p-4 md:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif text-lg text-[#1c1a17]">{j.title}</h3>
                    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-semibold ${j.status === "Open" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{j.status}</span>
                    <span className="inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-semibold bg-[#234333]/10 text-[#234333]">{j.employment_type}</span>
                  </div>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#234333]"><Building2 size={11} />{j.institute_name}{j.institute_slug ? <span className="text-[#8b8578] font-normal ml-1">/{j.institute_slug}</span> : null}</div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#6b655b]">
                    {j.location && <span className="inline-flex items-center gap-1"><MapPin size={11} />{j.location}</span>}
                    {j.salary_range && <span className="inline-flex items-center gap-1"><IndianRupee size={11} />{j.salary_range}</span>}
                    <span className="inline-flex items-center gap-1"><Users size={11} />{j.openings} opening{j.openings > 1 ? "s" : ""}</span>
                    {j.deadline && <span className="inline-flex items-center gap-1"><Calendar size={11} />Apply by {fmt(j.deadline)}</span>}
                    <span className="text-[#8b8578]">Posted {fmt(j.posted_at)}</span>
                  </div>
                  {j.qualification && <div className="mt-2 text-xs text-[#5b5650]"><span className="font-semibold text-[#1c1a17]">Qualification:</span> {j.qualification}</div>}
                  {j.description && <div className="mt-2 text-xs text-[#5b5650] line-clamp-2 whitespace-pre-line">{j.description}</div>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button" onClick={() => viewApps(j)} data-testid={`adm-job-apps-${j.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#234333] bg-[#234333]/10 hover:bg-[#234333]/20 px-3 py-1.5 rounded-sm"><FileText size={11} /> Applications</button>
                  <button type="button" onClick={() => openEdit(j)} data-testid={`adm-job-edit-${j.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#234333] hover:bg-[#234333]/10 px-2 py-1.5 rounded-sm"><Pencil size={11} /> Edit</button>
                  <button type="button" onClick={() => setConfirmDelete(j)} data-testid={`adm-job-delete-${j.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 hover:bg-red-50 px-2 py-1.5 rounded-sm"><Trash2 size={11} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#fbf8f1] max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{form.id ? "Edit Job" : "Post New Job"}</DialogTitle>
            <DialogDescription>Choose the institute this job belongs to. It will appear on that institute&apos;s public Careers page + on gsmandal.org/careers.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Institute *" full>
              <select data-testid="adm-job-input-institute" className={inputCls} value={form.institute_id} onChange={(e) => set("institute_id", e.target.value)}>
                <option value="">— Choose institute —</option>
                {institutes.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </Field>
            <Field label="Title *" full>
              <input data-testid="adm-job-input-title" type="text" maxLength={200} className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} />
            </Field>
            <Field label="Employment Type">
              <select data-testid="adm-job-input-type" className={inputCls} value={form.employment_type} onChange={(e) => set("employment_type", e.target.value)}>
                {EMPLOYMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <input data-testid="adm-job-input-location" type="text" maxLength={160} className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} />
            </Field>
            <Field label="Openings"><input data-testid="adm-job-input-openings" type="number" min={1} className={inputCls} value={form.openings} onChange={(e) => set("openings", e.target.value)} /></Field>
            <Field label="Deadline"><input data-testid="adm-job-input-deadline" type="date" className={inputCls} value={form.deadline} onChange={(e) => set("deadline", e.target.value)} /></Field>
            <Field label="Salary Range" full><input data-testid="adm-job-input-salary" type="text" maxLength={160} className={inputCls} value={form.salary_range} onChange={(e) => set("salary_range", e.target.value)} placeholder="e.g., ₹40,000 - ₹60,000 per month" /></Field>
            <Field label="Qualification / Eligibility" full><textarea data-testid="adm-job-input-qual" rows={2} maxLength={1000} className={`${inputCls} resize-y`} value={form.qualification} onChange={(e) => set("qualification", e.target.value)} /></Field>
            <Field label="Description" full><textarea data-testid="adm-job-input-desc" rows={5} maxLength={6000} className={`${inputCls} resize-y`} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
            <Field label="Status">
              <select data-testid="adm-job-input-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
              </select>
            </Field>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
            <button type="button" onClick={save} disabled={saving} data-testid="adm-job-save-btn" className="text-sm bg-[#234333] hover:bg-[#1a3326] text-white px-4 py-2.5 rounded-sm font-semibold disabled:opacity-60">{saving ? "Saving…" : (form.id ? "Update" : "Post Job")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!appsFor} onOpenChange={(o) => !o && (setAppsFor(null), setApps([]))}>
        <DialogContent className="bg-[#fbf8f1] max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Applications</DialogTitle>
            <DialogDescription>{appsFor?.title} · {apps.length} received</DialogDescription>
          </DialogHeader>
          {appsLoading ? (
            <div className="text-sm text-[#6b655b]">Loading…</div>
          ) : apps.length === 0 ? (
            <div className="bg-white border border-[#e5e0d8] rounded-sm p-8 text-center">
              <FileText size={30} className="mx-auto text-[#bcb6aa]" />
              <div className="mt-2 text-sm text-[#6b655b]">No applications yet.</div>
            </div>
          ) : (
            <div className="overflow-x-auto bg-white border border-[#e5e0d8] rounded-sm">
              <table className="w-full text-sm">
                <thead className="bg-[#f5f1e8] text-[10px] uppercase tracking-wider text-[#6b655b]">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Candidate</th>
                    <th className="text-left px-3 py-2 font-semibold">Contact</th>
                    <th className="text-left px-3 py-2 font-semibold">YOE</th>
                    <th className="text-left px-3 py-2 font-semibold">Current</th>
                    <th className="text-left px-3 py-2 font-semibold">Resume</th>
                    <th className="text-left px-3 py-2 font-semibold">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.id} className="border-t border-[#e5e0d8] align-top">
                      <td className="px-3 py-3 font-semibold text-[#1c1a17]">{a.full_name}</td>
                      <td className="px-3 py-3">
                        <a href={`tel:${a.mobile}`} className="inline-flex items-center gap-1 text-[#234333]"><Phone size={11} />{a.mobile}</a>
                        {a.email && <a href={`mailto:${a.email}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#6b655b] break-all"><Mail size={10} />{a.email}</a>}
                      </td>
                      <td className="px-3 py-3">{a.years_experience}</td>
                      <td className="px-3 py-3 text-xs text-[#5b5650] max-w-xs"><div className="line-clamp-3">{a.current_profession || "—"}</div></td>
                      <td className="px-3 py-3">
                        {a.resume_url ? <a href={a.resume_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#234333] text-[11px] font-semibold"><ExternalLink size={11} /> {a.resume_filename || "Resume.pdf"}</a> : "—"}
                      </td>
                      <td className="px-3 py-3 text-[11px] text-[#6b655b] whitespace-nowrap">{fmt(a.submitted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf8f1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete Job?</AlertDialogTitle>
            <AlertDialogDescription>“{confirmDelete?.title}” and all its applications will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
